import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, Observable, of, ReplaySubject, tap } from 'rxjs';
import { environment } from '@env/environment';
import {
    CustomTableRequest,
    CustomTableResponse,
    CustomTableTemplateInstantiationRequest,
    CustomTableTemplateMeta,
    SpringPage,
} from './indemnity-custom-table.interface';

@Injectable()
export class IndemnityCustomTableService {

    baseUrl = environment.pricing_admin_url + '/indemnity/custom-tables';

    private _table: ReplaySubject<CustomTableResponse | null> = new ReplaySubject<CustomTableResponse | null>(1);
    private _tables: ReplaySubject<CustomTableResponse[]> = new ReplaySubject<CustomTableResponse[]>(1);
    private _templates: ReplaySubject<CustomTableTemplateMeta[]> = new ReplaySubject<CustomTableTemplateMeta[]>(1);

    set table(value: CustomTableResponse | null) { this._table.next(value); }
    get table$(): Observable<CustomTableResponse | null> { return this._table.asObservable(); }

    set tables(value: CustomTableResponse[]) { this._tables.next(value); }
    get tables$(): Observable<CustomTableResponse[]> { return this._tables.asObservable(); }

    set templates(value: CustomTableTemplateMeta[]) { this._templates.next(value); }
    get templates$(): Observable<CustomTableTemplateMeta[]> { return this._templates.asObservable(); }

    constructor(private _httpClient: HttpClient) {}

    /** Paginated list of current versions (one row per unique name). */
    list(page: number = 0, size: number = 20, sort?: string, at?: Date): Observable<SpringPage<CustomTableResponse>> {
        let params = new HttpParams().set('page', page).set('size', size);
        if (sort) params = params.set('sort', sort);
        if (at) params = params.set('at', this.toInstantIso(at));
        return this._httpClient.get<SpringPage<CustomTableResponse>>(this.baseUrl, { params })
            .pipe(
                tap(p => { this.tables = p.content; }),
                catchError(() => of(this.emptyPage<CustomTableResponse>())),
            );
    }

    loadList(): void {
        this.list().subscribe();
    }

    /** Returns the version of {name} effective at {at} (default = now). */
    getCurrent(name: string, at?: Date): Observable<CustomTableResponse> {
        let params = new HttpParams();
        if (at) params = params.set('at', this.toInstantIso(at));
        return this._httpClient.get<CustomTableResponse>(`${this.baseUrl}/${encodeURIComponent(name)}/current`, { params })
            .pipe(tap(t => { this.table = t; }));
    }

    /** Returns paginated history of versions for {name}, newest first. */
    getHistory(name: string, page: number = 0, size: number = 20): Observable<SpringPage<CustomTableResponse>> {
        const params = new HttpParams().set('page', page).set('size', size);
        return this._httpClient.get<SpringPage<CustomTableResponse>>(`${this.baseUrl}/${encodeURIComponent(name)}/history`, { params })
            .pipe(catchError(() => of(this.emptyPage<CustomTableResponse>())));
    }

    /** Creates a new version (append-only). */
    create(payload: CustomTableRequest): Observable<CustomTableResponse> {
        return this._httpClient.post<CustomTableResponse>(this.baseUrl, payload)
            .pipe(tap(t => { this.table = t; }));
    }

    /** Creates the first version of a table from a built-in template. */
    createFromTemplate(payload: CustomTableTemplateInstantiationRequest): Observable<CustomTableResponse> {
        return this._httpClient.post<CustomTableResponse>(`${this.baseUrl}/from-template`, payload)
            .pipe(tap(t => { this.table = t; }));
    }

    /** Soft-deletes all versions of {name}. */
    delete(name: string): Observable<void> {
        return this._httpClient.delete<void>(`${this.baseUrl}/${encodeURIComponent(name)}`);
    }

    listTemplates(): Observable<CustomTableTemplateMeta[]> {
        return this._httpClient.get<CustomTableTemplateMeta[]>(`${this.baseUrl}/templates`)
            .pipe(
                tap(templates => { this.templates = templates; }),
                catchError(() => of([] as CustomTableTemplateMeta[])),
            );
    }

    loadTemplates(): void {
        this.listTemplates().subscribe();
    }

    /** Backend expects Instant (ISO 8601 UTC with Z). */
    private toInstantIso(d: Date): string {
        return d.toISOString();
    }

    private emptyPage<T>(): SpringPage<T> {
        return {
            content: [],
            pageable: { pageNumber: 0, pageSize: 0, offset: 0, paged: false, unpaged: true },
            totalElements: 0, totalPages: 0, last: true, first: true, number: 0,
            numberOfElements: 0, size: 0, empty: true,
        };
    }
}
